import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Building2, FileText, CheckCircle, ArrowRight, BookOpen, Scale, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';

const ReglementCopropriete = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Règlement de Copropriété : Guide Complet 2025 | Contenu, modification, application</title>
        <meta name="description" content="Tout savoir sur le règlement de copropriété : contenu obligatoire, modification, application. Guide complet pour comprendre ce document essentiel." />
        <meta name="keywords" content="règlement copropriété, règlement intérieur copropriété, modification règlement copropriété, parties communes, parties privatives" />
        <link rel="canonical" href="https://copropriete.app/reglement-de-copropriete" />
      </Helmet>

      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900">copropriete.app</span>
            </Link>
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => window.location.href = '/login'}>Se connecter</Button>
              <Button onClick={() => window.location.href = '/login'} className="bg-emerald-600 hover:bg-emerald-700 text-white">C'est gratuit</Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-indigo-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <BookOpen className="w-4 h-4" />
              Guide complet 2025
            </span>
            <h1 className="text-4xl sm:text-5xl font-black text-slate-900 mb-6">Règlement de Copropriété</h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Le document fondateur de votre copropriété : droits, obligations et règles de vie commune.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <main className="prose prose-slate prose-lg max-w-none">
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <Scale className="w-8 h-8 text-indigo-600" />
              Qu'est-ce que le règlement de copropriété ?
            </h2>
            <p>
              Le <strong>règlement de copropriété</strong> est le document juridique fondamental qui organise la vie de l'immeuble. Il est obligatoire pour toute copropriété et définit :
            </p>
            <ul>
              <li>La destination de l'immeuble (habitation, commerce, mixte)</li>
              <li>Les parties communes et les parties privatives</li>
              <li>Les règles de jouissance des parties communes et privatives</li>
              <li>La répartition des charges entre copropriétaires</li>
              <li>Les règles d'administration de la copropriété</li>
            </ul>

            <div className="bg-indigo-50 border-l-4 border-indigo-500 p-6 rounded-r-xl my-8">
              <p className="text-indigo-900 font-medium m-0">
                💡 <strong>Bon à savoir :</strong> Le règlement de copropriété est publié au fichier immobilier. Tout nouveau copropriétaire est réputé en avoir pris connaissance et doit le respecter.
              </p>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <Home className="w-8 h-8 text-indigo-600" />
              Contenu du règlement
            </h2>
            
            <h3 className="text-xl font-bold text-slate-800">Parties privatives</h3>
            <p>
              Ce sont les parties appartenant exclusivement à chaque copropriétaire : appartements, caves, parkings. Le règlement définit précisément ce qui est privatif.
            </p>

            <h3 className="text-xl font-bold text-slate-800">Parties communes</h3>
            <p>
              Les parties communes appartiennent à l'ensemble des copropriétaires : hall, escaliers, toiture, façades, jardins. Leur usage est réglementé.
            </p>

            <h3 className="text-xl font-bold text-slate-800">Tantièmes</h3>
            <p>
              Le règlement contient l'<strong>état descriptif de division</strong> qui attribue à chaque lot un nombre de tantièmes déterminant :
            </p>
            <ul>
              <li>La quote-part dans les parties communes</li>
              <li>La participation aux charges</li>
              <li>Le nombre de voix en assemblée générale</li>
            </ul>

            <h3 className="text-xl font-bold text-slate-800">Règles de vie</h3>
            <ul>
              <li>Horaires de travaux et nuisances sonores</li>
              <li>Usage des parties communes (vélos, poussettes)</li>
              <li>Animaux domestiques</li>
              <li>Activités professionnelles autorisées</li>
            </ul>
          </section>

          <section className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <FileText className="w-8 h-8 text-indigo-600" />
              Modification du règlement
            </h2>
            <p>
              Le règlement de copropriété peut être modifié en assemblée générale selon différentes majorités :
            </p>

            <h3 className="text-xl font-bold text-slate-800">Majorité de l'article 26 (double majorité)</h3>
            <ul>
              <li>Modification des règles de jouissance</li>
              <li>Changement de répartition des charges</li>
              <li>Suppression du poste de gardien</li>
            </ul>

            <h3 className="text-xl font-bold text-slate-800">Unanimité requise</h3>
            <ul>
              <li>Changement de destination de l'immeuble</li>
              <li>Modification des tantièmes</li>
              <li>Atteinte aux droits des copropriétaires sur leurs lots</li>
            </ul>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r-xl my-8">
              <p className="text-yellow-900 font-medium m-0">
                ⚠️ <strong>Attention :</strong> Toute modification du règlement doit être publiée au fichier immobilier pour être opposable aux tiers.
              </p>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Application du règlement</h2>
            <p>
              Le syndic est chargé de faire respecter le règlement de copropriété. En cas de non-respect :
            </p>
            <ol>
              <li><strong>Rappel à l'ordre</strong> - Courrier simple au copropriétaire fautif</li>
              <li><strong>Mise en demeure</strong> - Lettre recommandée</li>
              <li><strong>Action en justice</strong> - Le syndicat peut assigner le copropriétaire devant le tribunal</li>
            </ol>

            <p>
              Tout copropriétaire peut également agir individuellement contre un autre copropriétaire qui ne respecte pas le règlement.
            </p>
          </section>

          {/* CTA */}
          <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl p-8 my-8 border border-indigo-200">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Centralisez vos documents de copropriété</h3>
            <p className="text-slate-700 mb-6">
              Avec copropriete.app, stockez et partagez facilement le règlement de copropriété et tous vos documents importants.
            </p>
            <Button onClick={() => window.location.href = '/login'} size="lg" className="bg-indigo-600 hover:bg-indigo-700">
              Essayer gratuitement pendant 7 jours
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </main>
      </div>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-slate-950 text-slate-400">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-white">copropriete.app</span>
          </Link>
          <p className="text-sm">© 2025 copropriete.app. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
};

export default ReglementCopropriete;
